import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useVideoGrid } from "../hooks/useVideoGrid";

type VideoGridProps = {
  moodImages: [any];
  relevantMoods: any;
  videosReady: any;
  onMoodClick: any;
  theme: any;
  collection: any;
};

const VideoGrid: React.FC<VideoGridProps> = ({
  moodImages,
  relevantMoods,
  videosReady,
  onMoodClick,
  theme,
  collection,
}) => {
  const { t } = useTranslation();
  const {
    videoRefs,
    loadedVideos,
    moodToPathMap,
    placeholdersPreloaded,
    handleVideoLoaded,
    handleVideoError,
  } = useVideoGrid({
    moodImages,
    videosReady,
    theme: theme,
    collection: collection,
  });

  const [loadedPlaceholders, setLoadedPlaceholders] = useState(() =>
    placeholdersPreloaded ? new Set([0, 1, 2, 3]) : new Set()
  );

  // Start every tile blank (white) and only fade it in after the first paint,
  // so the grid never appears instantly even when images are cached.
  const [revealed, setRevealed] = useState(false);
  const [forceVisible, setForceVisible] = useState(false);
  useEffect(() => {
    const fallback = setTimeout(() => setForceVisible(true), 2000);
    return () => clearTimeout(fallback);
  }, []);
  useEffect(() => {
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setRevealed(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, []);

  const getDurationLabel = (duration: number) => {
    if (duration === 1) return t(`homepage.1-3`);
    if (duration === 2) return t(`homepage.3-6`);
    if (duration === 4) return t(`homepage.entireDay`);
    return t(`homepage.entireDay`);
  };

  const first4 = moodImages.slice(0, 4);

  const pathsReady = first4.every((m) => {
    const pd = moodToPathMap.get(m.enLabel);
    return Boolean(pd?.placeholder || m.src);
  });

  const gridPadding = {
    paddingTop: "16px",
    paddingLeft: "16px",
    paddingRight: "16px",
  };
  // The grid sits in a shrink-to-fit parent (the drawer trigger button), and
  // the absolutely positioned media no longer give it an intrinsic width, so
  // give it an explicit one.
  const gridWidth = {
    width: "360px",
    maxWidth: "100%",
    boxSizing: "border-box",
  } as const;

  // Same wrapper and tile box as the loaded grid, so nothing shifts when the
  // images arrive and fade in.
  if (!pathsReady) {
    return (
      <div style={gridPadding}>
        <div className="mood-selection-container" style={gridWidth}>
          {first4.map((_, i) => (
            <div
              key={i}
              className="mood-item"
              style={{ backgroundColor: "#FFFFFF" }}
            >
              <div
                className="mood-item-inner"
                style={{ backgroundColor: "#FFFFFF" }}
              />
              <p className="mood-text">&nbsp;</p>
              <div className="mood-duration-div-outer">
                <div style={{ visibility: "hidden" }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={gridPadding}>
      <div className={`mood-selection-container`} style={gridWidth}>
        {first4.map(({ label, enLabel, src }, feelingName) => {
          const pathData = moodToPathMap.get(enLabel);
          const videoSrc = pathData?.video || "";
          const placeholderSrc = pathData?.placeholder || src || "";
          const isTileVisible =
            revealed &&
            (forceVisible ||
              loadedPlaceholders.has(feelingName) ||
              loadedVideos.has(feelingName));
          const fadeStyle = {
            opacity: isTileVisible ? 1 : 0,
            transition: `opacity 1.4s ease-in-out ${feelingName * 0.25}s`,
          };

          return (
            <div
              key={feelingName}
              className="mood-item"
              style={{ backgroundColor: "#FFFFFF" }}
              onClick={() => onMoodClick(feelingName)}
            >
              <div
                className="mood-item-inner"
                style={{ backgroundColor: "#FFFFFF" }}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    ...fadeStyle,
                  }}
                >
                  {placeholderSrc && (
                    <>
                      <img
                        src={placeholderSrc}
                        alt={label}
                        className="mood-image"
                        loading="eager"
                        fetchPriority="high"
                        decoding="async"
                        ref={(el) => {
                          if (
                            el?.complete &&
                            el.naturalWidth > 0 &&
                            !loadedPlaceholders.has(feelingName)
                          ) {
                            setLoadedPlaceholders(
                              (prev) => new Set([...prev, feelingName])
                            );
                          }
                        }}
                        onLoad={() => {
                          setLoadedPlaceholders(
                            (prev) => new Set([...prev, feelingName])
                          );
                        }}
                        style={{
                          opacity: loadedVideos.has(feelingName) ? 0 : 1,
                          visibility: loadedVideos.has(feelingName)
                            ? "hidden"
                            : "visible",
                          transition: loadedVideos.has(feelingName)
                            ? "opacity 0.2s ease-out, visibility 0s linear 0.2s"
                            : "opacity 0.2s ease-in",
                          position: "absolute",
                          inset: 0,
                          pointerEvents: loadedVideos.has(feelingName)
                            ? "none"
                            : "auto",
                          willChange: loadedVideos.has(feelingName)
                            ? "opacity"
                            : "auto",
                        }}
                      />
                    </>
                  )}

                  {videoSrc && (
                    <video
                      ref={(el) => {
                        videoRefs.current[feelingName] = el;
                      }}
                      src={videoSrc}
                      className="mood-video"
                      preload="auto"
                      loop
                      muted
                      playsInline
                      autoPlay={videosReady}
                      onLoadedData={() => handleVideoLoaded(feelingName)}
                      onCanPlayThrough={() => handleVideoLoaded(feelingName)}
                      onError={() => handleVideoError(feelingName)}
                      onMouseEnter={async () => {
                        const video = videoRefs.current[feelingName];
                        if (video) {
                          try {
                            await video.play();
                          } catch (error) {}
                        }
                      }}
                      style={{
                        opacity: loadedVideos.has(feelingName) ? 1 : 0,
                        visibility: loadedVideos.has(feelingName)
                          ? "visible"
                          : "hidden",
                        transition: loadedVideos.has(feelingName)
                          ? "opacity 0.2s ease-in"
                          : "opacity 0s",
                        width: "100%",
                        height: "auto",
                        pointerEvents: loadedVideos.has(feelingName)
                          ? "auto"
                          : "none",
                        willChange: loadedVideos.has(feelingName)
                          ? "opacity"
                          : "auto",
                      }}
                    />
                  )}
                </div>
              </div>
              <p className="mood-text" style={fadeStyle}>
                {label}
              </p>
              <div className="mood-duration-div-outer" style={fadeStyle}>
                {relevantMoods[feelingName] ? (
                  <div className="mood-duration-div-inner">
                    {getDurationLabel(relevantMoods[feelingName])}
                  </div>
                ) : (
                  <div style={{ visibility: "hidden" }} />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VideoGrid;
